/**
 * The parked L's live link (M10 P5a11a part 2): the logic behind `features/parkedL/feature.ts`, over narrow deps so
 * a test drives it with a real sim. See the feature's header for the rules. `sync` is the per-frame entry: a
 * reference compare on the Career and the system id (allocation-free, D-014) and an event-rate reconcile when either
 * changed. The control stage (`stepParkedL`, P5a10b: hold or escort) allocates nothing.
 */
import type { ShipStream } from '../../assets/shipStream'
import type { Career, ParkedL } from '../../career/types'
import { logger } from '../../core/logger'
import type { AssetsFile } from '../../data/assetSchema'
import { cradleSocketOf } from '../../docking/cradle'
import { flownEntry } from '../../fleet-link/hangar'
import { loseParkedL, noteBoardedShipLost } from '../../fleet-link/loss'
import {
  parkedJumpRefusal,
  parkedLConfig,
  parkedLRecord,
  releaseParkedL,
  setParkedOrder,
  settleCarried,
  spawnParkedL,
  stepParkedL,
  type ParkedKeepOut,
  type ParkedLBody,
} from '../../fleet-link/parkedL'
import type { FleetOutcome, FleetService } from '../../fleet-link/service'
import { despawnShip, registerSimStage, setShipPose, type Sim } from '../../sim'
import type { DockingRuntime } from '../../space/contracts'
import { AngVel, Pos64, Rot, Vel } from '../../world/components'
import { launchClearPoint } from './swap'

/** Which pair actions apply now (the HUD/station buttons read this; all false without an owned L in the pair). */
export interface ParkedLActions {
  /** Flying the S docked back in the L's cradle: "Unload to the <L>" and "Take the helm". */
  unload: boolean
  takeHelm: boolean
  /** Flying the L with an S in its cradle: launch the S from the helm. */
  launch: boolean
  /** Flying an L with a free cradle: stow a fleet S docked here (the station check is the service's). */
  stow: boolean
}

/** The pair actions for `career` (pure; event-rate). */
export function parkedLActions(career: Career): ParkedLActions {
  const cradle = flownEntry(career)?.cradle
  const carried = career.carriedBy !== undefined
  return {
    unload: carried,
    takeHelm: carried,
    launch: !carried && cradle !== undefined && cradle.carried !== null,
    stow: !carried && cradle !== undefined && cradle.carried === null,
  }
}

/** The pair actions wired to part 1's fleet service calls; null when the action doesn't apply. */
export interface ParkedLService {
  actions(): ParkedLActions
  unload(): FleetOutcome | null
  takeHelm(): Promise<FleetOutcome> | null
  launchFromHelm(): Promise<FleetOutcome> | null
  stow(shipId: string, stationId: string): Promise<FleetOutcome> | null
  /** The live parked L's entity, or −1 (tests, the HUD). */
  bodyEid(): number
}

export type ParkedLFleet = Pick<FleetService, 'dockBackToCarrier' | 'relaunchFromCarrier' | 'unloadToCarrier' | 'takeHelm' | 'launchFromHelm' | 'stowInCarrier'>

export interface ParkedLLinkDeps {
  readonly sim: Sim
  /** `cradleStatus` (W5 merge): a cradle carrier registered outside this link (the dev `spawnOwnL`) keeps `carriedBy`. */
  readonly docking: Pick<DockingRuntime, 'setCradleCarrier' | 'cradleStatus'>
  readonly assets: Pick<AssetsFile, 'models'>
  readonly fleet: ParkedLFleet
  /** The ship stream (F8: the pair's models load before the L appears); absent: spawn at once. */
  readonly ships?: Pick<ShipStream, 'ensure' | 'colliderOf' | 'assetsOfDefs'>
  career(): Career
  /** The system the player is in and ready, else null (between systems). */
  systemId(): string | null
  /** P5a10b: whether the L has a hired captain (`parkedLCaptained` over the worker fleet view); absent: none. */
  captained?(fleetId: string): Promise<boolean>
  /** P5a10b: the escort's keep-out spheres (field clumps); absent or null: none. Fixed (tests); see `keepOutOf`. */
  readonly keepOut?: ParkedKeepOut | null
  /**
   * M10 P5a11b: a Career write (the career runtime's `applyDevCommand`), for the L's loss in space and the release
   * of a carried state with nothing carried (review C2). Absent: neither is written (logged).
   */
  writeCareer?(name: string, cmd: (c: Career) => Career): void
  /**
   * M10 P5a11c: the system's keep-out spheres (its field clumps, packed float64 world `x, y, z, r`), read once on
   * each system change (event-rate), never per frame. Ignored when `keepOut` is given.
   */
  keepOutOf?(systemId: string): Float64Array | null
  /** M10 P5a11c: the flown ship swapped (Take the helm, Launch from the helm): the camera snaps. */
  onSwap?(): void
  /** M10 P5a11c: how far out of the cradle mouth the S appears after Launch from the helm (m). */
  readonly launchClearM?: number
}

export interface ParkedLLink {
  readonly service: ParkedLService
  /** Per frame: allocation-free unless the Career or the system changed. */
  sync(): void
  /** Q5 for the relay path: the refusal text for a jump out of `systemId`, or null. */
  refusal(systemId: string): string | null
  body(): ParkedLBody | null
  /** M10 P5a11c: the keep-out spheres the control stage passes to `stepParkedL` now (null: none). */
  keepOut(): Readonly<ParkedKeepOut> | null
  /** M10 P5a11c: whether the live L has a hired captain (the order choices read it); false without a body. */
  captained(): boolean
  /** M10 P5a11c: re-reads the captain (a fleet-view change); a no-op without a body or a captain source. */
  recheckCaptain(): void
  /** Resolves when no spawn waits on a model load (tests). */
  idle(): Promise<void>
  dispose(): void
}

/** Scratch for the event-rate swaps. */
const swapQ = { x: 0, y: 0, z: 0, w: 1 }
const swapP = { x: 0, y: 0, z: 0 }

function nameOf(career: Career, fleetId: string, sim: Sim): string {
  const e = (career.hangar ?? []).find((h) => h.fleetId === fleetId)
  if (e?.name !== undefined) return e.name
  return sim.defs.find((d) => d.id === e?.defId)?.name ?? 'your L'
}

export function createParkedLLink(deps: ParkedLLinkDeps): ParkedLLink {
  const { sim, docking, fleet } = deps
  const cfg = parkedLConfig()
  let body: ParkedLBody | null = null
  let spawning: Promise<void> | null = null
  /** Set while the link releases the carrier itself, so the release's `onCarried(undefined)` writes nothing. */
  let releasing = false
  let lastCareer: Career | null = null
  let lastSystem: string | null = null
  /** P5a11b: the parked L that died in the control stage, for the next `sync` (the Career write is event-rate). */
  let lostId: string | null = null
  /** P5a11b: the L's pose when the link last released it while it carried the flown S (a system change). */
  let lastPose: ParkedL | null = null

  const release = (): void => {
    const b = body
    if (b === null) return
    if (deps.career().carriedBy === b.fleetId) lastPose = parkedLRecord(b)
    releasing = true
    try {
      releaseParkedL({ docking })
    } finally {
      releasing = false
    }
    despawnShip(sim, b.eid)
    body = null
  }

  const onCarried = (id: string | undefined): void => {
    if (releasing) return
    if (id !== undefined) {
      const r = fleet.dockBackToCarrier(id)
      if (!r.ok) logger.warn(`parkedL: dock back to ${id} refused (${r.error})`)
      return
    }
    const b = body
    if (b === null) return
    const r = fleet.relaunchFromCarrier(parkedLRecord(b))
    if (!r.ok) logger.warn(`parkedL: relaunch from ${b.fleetId} refused (${r.error})`)
    // The L parks where it is now: its hold law's target moves there.
    b.target.x = Pos64.x[b.eid]
    b.target.y = Pos64.y[b.eid]
    b.target.z = Pos64.z[b.eid]
  }

  /** The captain read for `b` (event-rate: spawn and fleet-view changes); a stale answer is dropped. */
  const readCaptain = (b: ParkedLBody): void => {
    deps.captained?.(b.fleetId).then(
      (c) => {
        if (body === b) setParkedOrder(b, b.order, c)
      },
      (err: unknown) => logger.warn(`parkedL: captain read for ${b.fleetId} failed`, err),
    )
  }

  /** M10 P5a11c: the keep-out spheres of system `sys` (event-rate, on a system change). */
  let keepOut: ParkedKeepOut | null = deps.keepOut ?? null
  const refreshKeepOut = (sys: string | null): void => {
    if (deps.keepOut !== undefined && deps.keepOut !== null) return
    const s = sys === null ? null : (deps.keepOutOf?.(sys) ?? null)
    keepOut = s === null || s.length < 4 ? null : { spheres: s, count: Math.floor(s.length / 4) }
  }

  /** M10 P5a11c, Take the helm: the player moves into the L's pose and the parked body goes (no relaunch write). */
  const swapIntoL = (b: ParkedLBody | null): void => {
    const p = sim.playerEid
    if (b === null || p < 0 || body !== b) return
    const e = b.eid
    const x = Pos64.x[e]
    const y = Pos64.y[e]
    const z = Pos64.z[e]
    swapQ.x = Rot.x[e]
    swapQ.y = Rot.y[e]
    swapQ.z = Rot.z[e]
    swapQ.w = Rot.w[e]
    const vx = Vel.x[e]
    const vy = Vel.y[e]
    const vz = Vel.z[e]
    release()
    setShipPose(sim, p, x, y, z, swapQ, vx, vy, vz)
    AngVel.x[p] = AngVel.y[p] = AngVel.z[p] = 0
    deps.onSwap?.()
  }

  /** M10 P5a11c, Launch from the helm: the S appears out of the L's cradle mouth (the L parks where it was). */
  const swapOutOfL = (defId: string, pos: readonly [number, number, number], rot: readonly [number, number, number, number]): void => {
    const p = sim.playerEid
    if (p < 0) return
    const socket = cradleSocketOf(defId, { ships: sim.defs, assets: deps.assets })
    swapQ.x = rot[0]
    swapQ.y = rot[1]
    swapQ.z = rot[2]
    swapQ.w = rot[3]
    launchClearPoint(pos, swapQ, socket, deps.launchClearM ?? 0, swapP)
    setShipPose(sim, p, swapP.x, swapP.y, swapP.z, swapQ, 0, 0, 0)
    deps.onSwap?.()
  }

  const spawn = (career: Career): void => {
    const parked = career.parkedL
    if (parked === undefined) return
    const entry = (career.hangar ?? []).find((h) => h.fleetId === parked.fleetId)
    const defIndex = entry === undefined ? -1 : sim.defs.findIndex((d) => d.id === entry.defId)
    if (entry === undefined || defIndex < 0) {
      logger.warn(`parkedL: ${parked.fleetId} has no hangar def; not spawned`)
      return
    }
    const def = sim.defs[defIndex]
    const go = (): void => {
      spawning = null
      lastCareer = null // re-check on the next sync against the Career as it is then
      const now = deps.career().parkedL
      if (body !== null || now === undefined || now.fleetId !== parked.fleetId || deps.systemId() !== now.systemId) return
      const b = deps.assets.models.find((m) => m.id === def.assetId)?.bounds
      const radius = b ? Math.hypot(b.center[0], b.center[1], b.center[2]) + b.radius : def.colliderRadius
      const spawned = spawnParkedL(
        { sim, docking, socket: cradleSocketOf(def.id, { ships: sim.defs, assets: deps.assets }), radius, collider: deps.ships?.colliderOf(def.assetId) ?? null, onCarried },
        now,
        defIndex,
      )
      body = spawned
      // P5a10b: the captain read (escort needs one); until it answers, the L holds.
      readCaptain(spawned)
    }
    // F8: the pair's models (the L and the S it may carry) load before the L appears, so a reload shows no stand-in.
    const ships = deps.ships
    const ids = ships?.assetsOfDefs([entry.defId, career.ship.defId], sim.defs) ?? []
    if (ships === undefined || ids.length === 0) {
      go()
      return
    }
    spawning = ships.ensure(ids).then(go, (err: unknown) => {
      logger.warn(`parkedL: the pair's models failed to load; spawning anyway`, err)
      go()
    })
  }

  const write = (name: string, cmd: (c: Career) => Career): void => {
    if (deps.writeCareer === undefined) logger.warn(`parkedL: ${name} not written (no Career writer)`)
    else deps.writeCareer(name, cmd)
  }

  const reconcile = (career: Career, sys: string | null): void => {
    const parked = career.parkedL
    const b = body
    if (b !== null) {
      const parkedHere = parked !== undefined && parked.fleetId === b.fleetId && parked.systemId === sys
      const carrying = career.carriedBy === b.fleetId && b.systemId === sys
      if (!parkedHere && !carrying) release()
      // P5a10b: the panel's order change (`withParkedOrder`) reaches the live body.
      else if (parkedHere && parked.order !== b.order) setParkedOrder(b, parked.order, b.captained)
    }
    if (body === null && spawning === null && parked !== undefined && sys !== null && parked.systemId === sys) spawn(career)
    // P5a11b (C2): `carriedBy` with no L carrying it here (a reload, a system change): release it cleanly, the L
    // parked where it was last seen (or docked at the pilot's station when that is unknown; `settleCarried`).
    // Not while the docking runtime still carries the S in some other carrier's cradle (the dev `spawnOwnL` L).
    if (career.carriedBy !== undefined && body === null && spawning === null && docking.cradleStatus?.().carried !== true) {
      const pose = lastPose
      lastPose = null
      write('parkedL release carried', (c) => settleCarried(c, pose))
    }
  }

  const offStage = registerSimStage(sim, 'control', (x) => {
    // P5a10b: hold or escort (the flown S leads).
    if (body !== null && !stepParkedL(body, x, cfg, x.playerEid, keepOut)) {
      // P5a11b: the L died (the cradle runtime releases a carried S itself, D-1086 C3); the next sync writes the loss.
      lostId = body.fleetId
      releaseParkedL({ docking })
      body = null
    }
  })

  const service: ParkedLService = {
    actions: () => parkedLActions(deps.career()),
    unload: () => (parkedLActions(deps.career()).unload ? fleet.unloadToCarrier() : null),
    takeHelm: () => {
      if (!parkedLActions(deps.career()).takeHelm) return null
      const b = body
      return fleet.takeHelm().then((r) => {
        if (r.ok) swapIntoL(b)
        return r
      })
    },
    launchFromHelm: () => {
      const c = deps.career()
      const p = sim.playerEid
      const sys = deps.systemId()
      const flown = flownEntry(c)
      if (!parkedLActions(c).launch || p < 0 || sys === null || flown === null) return null
      // The L parks where the player flies it now.
      const pos: [number, number, number] = [Pos64.x[p], Pos64.y[p], Pos64.z[p]]
      const rot: [number, number, number, number] = [Rot.x[p], Rot.y[p], Rot.z[p], Rot.w[p]]
      return fleet.launchFromHelm({ fleetId: flown.fleetId, systemId: sys, pos, rot, order: 'hold' }).then((r) => {
        if (r.ok) swapOutOfL(flown.defId, pos, rot)
        return r
      })
    },
    stow: (shipId, stationId) => (parkedLActions(deps.career()).stow ? fleet.stowInCarrier(shipId, stationId) : null),
    bodyEid: () => body?.eid ?? -1,
  }

  return {
    service,
    sync(): void {
      if (lostId !== null) {
        const id = lostId
        lostId = null
        // P5a11b: the parked L's loss: the Career drops it, the worker frees its CARRIED_UNTIL slot (and pays a claim).
        write('parkedL lost', (c) => loseParkedL(c, id))
        noteBoardedShipLost(id)
      }
      const career = deps.career()
      const sys = deps.systemId()
      if (career === lastCareer && sys === lastSystem) return
      if (sys !== lastSystem) refreshKeepOut(sys)
      lastCareer = career
      lastSystem = sys
      reconcile(career, sys)
    },
    refusal(systemId: string): string | null {
      const c = deps.career()
      const parked = c.parkedL
      if (parked === undefined) return null // the system check is parkedJumpRefusal's
      // Moored: a parked L has no ring mooring yet (orders `hold`/`escort`; P5a8a moors the flown ship only), so a
      // `Career.parkedL` is always in open space; an L moored or docked at a station is not a `parkedL`.
      return parkedJumpRefusal(parked, systemId, false, nameOf(c, parked.fleetId, sim))
    },
    body: () => body,
    keepOut: () => keepOut,
    captained: () => body?.captained ?? false,
    recheckCaptain(): void {
      if (body !== null) readCaptain(body)
    },
    idle: () => spawning ?? Promise.resolve(),
    dispose(): void {
      release()
      offStage()
    },
  }
}
